import React, { useState } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 1000px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  padding: 60px 100px;
  margin-bottom: 0px;
  
  ${mediaQueries.large} {
    padding: 50px 100px;
  }
  
  ${mediaQueries.medium} {
    padding: 40px 40px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 20px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 40px;
  text-align: right;
  line-height: 1.1;
`;

const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 0px;
`;

const PageSubtitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 30px;
`;

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  max-width: 800px;
  margin-bottom: 20px;
`;

const DescriptionFinal = styled.p`
  font-size: 18px;
  line-height: 1.6;
  font-weight: 700;
`;

const HighlightedText = styled.span`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 700;
`;

const ProjectsSection = styled.section`
  padding: 60px 40px;
  background-color: #FEF2F1;
  border-radius: 32px;
  margin: 0 auto 60px;
  
  ${mediaQueries.large} {
    padding: 40px 30px;
  }
  
  ${mediaQueries.medium} {
    padding: 30px 20px;
  }
  
  ${mediaQueries.small} {
    padding: 20px 15px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 0px;
  text-align: center;
  width: 100%;
  color: ${({theme}) => theme.colors.secondary};
`;

const SectionSubtitle = styled.h3`
  font-size: 36px;
  margin-bottom: 40px;
  text-align: center;
  width: 100%;
  color: ${({theme}) => theme.colors.primary};
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
`;

const FilterLabel = styled.span`
  font-size: 16px;
  margin-right: 15px;
  display: flex;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid #e0e0e0;
  background-color: white;
  font-size: 16px;
  outline: none;
  cursor: pointer;
  
  &:focus {
    border-color: ${({theme}) => theme.colors.secondary};
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  
  ${mediaQueries.large} {
    grid-template-columns: repeat(2, 1fr);
  }
  
  ${mediaQueries.medium} {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const ArrowIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const ProjectContent = styled.div`
  padding: 30px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ProjectLogo = styled.img`
  max-height: 40px;
  margin-bottom: 15px;
  object-fit: contain;
  align-self: flex-start;
`;

const ProjectLink = styled.a`
  color: ${({theme}) => theme.colors.secondary};
  margin-top: 15px;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  
  &:hover {
    text-decoration: underline;
  }
  
  &::after {
    content: "→";
    margin-left: 5px;
    transition: transform 0.2s;
  }
  
  &:hover::after {
    transform: translateX(3px);
  }
`;

const ProjectTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 10px;
  font-weight: var(--font-weight-semibold);
`;

const ProjectDescription = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 20px;
  flex-grow: 1;
`;

const ProjectMeta = styled.div`
  font-size: 14px;
  color: #777;
  margin-top: auto;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const PaginationButton = styled.button`
  background-color: ${props => props.disabled ? '#f0f0f0' : 'white'};
  color: ${props => props.disabled ? '#aaa' : '#011D3A'};
  border: 1px solid #e0e0e0;
  padding: 10px 20px;
  border-radius: 30px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  font-weight: 500;
  display: flex;
  align-items: center;
  margin: 0 5px;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: ${props => props.disabled ? '#f0f0f0' : '#f8f8f8'};
  }
  
  &.next {
    background-color: #011D3A;
    color: white;
    
    &:hover {
      background-color: #01152b;
    }
  }
`;

const ContactSection = styled.section`
  padding: 60px 20px;
  background-color: #f8f8f8;
  text-align: center;
  margin-bottom: 0;
  border-radius: 32px 32px 0 0;
`;

const ContactTitle = styled.h2`
  font-size: 32px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 20px;
`;

const ContactDescription = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
`;

const ContactButton = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 30px;
  border-radius: 32px;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  display: inline-block;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const Projets: React.FC = () => {
  const [currentFilter, setCurrentFilter] = useState('all');
  
  return (
    <PageContainer>
      <SEO 
        title="Nos projets et succès" 
        description="Découvrez nos succès dans le développement de solutions logicielles pour nos clients. Projets réalisés dans le domaine de la santé, des RH et du e-commerce."
        canonical={`${window.location.origin}/projets`}
        image="/images/projets/logo-impulsion.png"
      />
      <Header backgroundColor="transparent" textColor="#011D3A" />
      
      <MainSection>
        <HeroSection>
          <TitleContainer>
            <PageTitle>
              <HighlightedText>We build success.</HighlightedText>
            </PageTitle>
            <PageSubtitle>Une collaboration créatrice de valeur.</PageSubtitle>
          </TitleContainer>
          
          <Description>
          Nous concevons plus que du code – nous façonnons des succès durables.
          Notre équipe s'associe avec des clients de toutes tailles, des startups innovantes
          aux grandes entreprises établies, pour transformer les défis numériques en opportunités de croissance.
          </Description>
          
          <Description>
            Nous croyons qu'un développement logiciel réussi repose sur une
            compréhension profonde de vos objectifs business. C'est pourquoi
            notre approche collaborative place vos enjeux stratégiques au cœur
            de chaque ligne de code que nous écrivons.
          </Description>
          
          <DescriptionFinal>
            Imaginez votre projet, nous sommes là pour <HighlightedText>vous aider à le construire.</HighlightedText>
          </DescriptionFinal>
        </HeroSection>
        
        <ProjectsSection>
          <SectionTitle>Nos réussites communes.</SectionTitle>
          <SectionSubtitle>Des succès mesurables et durables.</SectionSubtitle>

          
          <ProjectsGrid>
            <ProjectCard>
              <ProjectContent>
                <ProjectLogo src="/images/projets/logo-impulsion.png" alt="Logo Impulsion" />
                <ProjectTitle>La solution logicielle de saisie de comptes rendus structurés</ProjectTitle>
                <ProjectDescription>
                Un projet national porté par l'AFAQAP (Association Française d'Assurance Qualité en Anatomie Pathologique)
                où Thinkfab intervient comme partenaire technique principal. Notre équipe <strong>a conçu et réalisé la suite logicielle complète </strong>  
                dédiée aux pathologistes français, comprenant trois plateformes (REPO, PULSE, CUBE)<br/>
                Cette solution permet de structurer, produire et exploiter des comptes rendus médicaux standardisés, simplifiant le quotidien des praticiens tout en valorisant leurs données médicales. Le projet prépare ainsi l'avenir de la pathologie en France au bénéfice des patients.
                </ProjectDescription>
                <ProjectMeta>Santé</ProjectMeta>
                <ProjectLink href="https://impulsion-acp.fr" target="_blank" rel="noopener noreferrer">Voir le projet</ProjectLink>
              </ProjectContent>
            </ProjectCard>

            <ProjectCard>
              <ProjectContent>
                <ProjectLogo src="/images/projets/logo-colnechealth.png" alt="Colnec Health" />
                <ProjectTitle>Solution digitale de collaboration et coordination des soins pour l’ensemble des professionnels de santé</ProjectTitle>
                <ProjectDescription>
                Une entreprise innovante dans le domaine de la santé pour laquelle nous avons entièrement redéveloppé
                la plateforme existante, en y ajoutant une application mobile centrée sur l'expérience patient.<br />
                Notre intervention a permis de <strong>moderniser l'infrastructure technique tout en améliorant significativement
                l'ergonomie et les fonctionnalités du service</strong>.<br />
                Grâce à notre expertise en développement mobile et web,
                les utilisateurs bénéficient désormais d'une solution intuitive et performante qui facilite
                leur parcours de soins. Cette refonte complète a renforcé le positionnement de Colnec
                dans l'écosystème de la santé numérique, offrant une expérience fluide et sécurisée pour les patients
                comme pour les professionnels.
                </ProjectDescription>
                <ProjectMeta>Santé</ProjectMeta>
                <ProjectLink href="https://colnec.com" target="_blank" rel="noopener noreferrer">Voir le projet</ProjectLink>
              </ProjectContent>
            </ProjectCard>

            <ProjectCard>
              <ProjectContent>
                <ProjectLogo src="/images/projets/logo-logitio.png" alt="Logo Logitio" />
                <ProjectTitle>Un SIRH innovant conçu pour développer les compétences</ProjectTitle>
                <ProjectDescription>
                Une plateforme RH complète <strong>co-conçue et co-développée</strong>, articulée autour 
                e cinq univers digitaux interconnectés (Habilitation, Formation, Compétence, Entretien et Objectif)
                pour optimiser la gestion des talents.<br />
                La solution LogitioRH permet aux entreprises de digitaliser
                leurs processus RH, d'engager leurs collaborateurs dans leur développement professionnel
                et d'offrir aux managers les outils nécessaires pour piloter efficacement les compétences
                de leurs équipes.<br />L'accompagnement continu depuis la conception jusqu'au déploiement
                a permis de créer une application intuitive qui automatise les tâches administratives
                tout en offrant une vision stratégique décloisonnée du capital humain.
                </ProjectDescription>
                <ProjectMeta>RH</ProjectMeta>
                <ProjectLink href="https://logitio.com" target="_blank" rel="noopener noreferrer">Voir le projet</ProjectLink>
              </ProjectContent>
            </ProjectCard>

            <ProjectCard>
              <ProjectContent>
                <ProjectLogo src="/images/projets/logo-hopi.svg" alt="Logo HopiMedical" />
                <ProjectTitle>Quand la télémédecine offre l'examen physique complet</ProjectTitle>
                <ProjectDescription>
                Un acteur majeur de la santé numérique pour lequel nous avons contribué à
                des innovations stratégiques comme le <strong>dossier médical partagé</strong> et
                la <strong>solution d'agrégation de données biologiques</strong>.
                Notre expertise a permis de concevoir des architectures robustes et évolutives répondant
                aux exigences strictes du secteur médical en matière de sécurité et d'interopérabilité.
                <br />Notre <strong>conseil en architecture</strong> globale a aidé Hopi à construire un écosystème technologique
                cohérent, facilitant le partage sécurisé d'informations entre professionnels de santé
                tout en respectant la confidentialité des données patients. Cette collaboration a contribué
                à améliorer le parcours de soins et l'efficacité des échanges dans le système de santé français.
                </ProjectDescription>
                <ProjectMeta>Santé</ProjectMeta>
                <ProjectLink href="https://hopimedical.com" target="_blank" rel="noopener noreferrer">Voir le projet</ProjectLink>
              </ProjectContent>
            </ProjectCard>

            <ProjectCard>
              <ProjectContent>
                <ProjectLogo src="/images/projets/logo-cybernecard.png" alt="Logo Cybernecard" />
                <ProjectTitle>Distributeur d'objets textiles et tendances</ProjectTitle>
                <ProjectDescription>
                Un distributeur spécialisé avec 30 ans d'expertise dans les textiles professionnels et
                objets promotionnels, dont nous avons <strong>transformé l'infrastructure digitale par la création
                d'un ERP marketing complet, d'une plateforme e-commerce performante et d'un système EDI intégré
                pour la gestion des commandes fournisseurs</strong>. Partenaire de marques prestigieuses comme Daiber,
                CXS et Halfar, Cybernecard est certifiée EcoVadis Platinum (top 1% du secteur) et offre
                une expérience client fluide et des processus d'approvisionnement optimisés grâce
                à notre solution sur mesure.<br />Notre collaboration a permis à Cybernecard de renforcer
                son positionnement premium tout en simplifiant son écosystème numérique.
                </ProjectDescription>
                <ProjectMeta>E-commerce, ERP • B2B</ProjectMeta>
                <ProjectLink href="https://cybernecard.fr" target="_blank" rel="noopener noreferrer">Voir le projet</ProjectLink>
              </ProjectContent>
            </ProjectCard>

            <ProjectCard>
              <ProjectContent>
                <ProjectLogo src="/images/projets/logo-fashop.png" alt="Logo Fashop" />
                <ProjectTitle>Déployez votre marque dans un écosystème ciblé</ProjectTitle>
                <ProjectDescription>
                Un acteur clé du secteur de la mode pour lequel nous avons co-conçu et développé la première version de leur <strong>plateforme
                de prospection digitales</strong>, devenue aujourd'hui référence dans l'industrie.
                Notre collaboration a permis de créer un système innovant qui facilite la rencontre entre
                grandes marques et boutiques multimarques françaises en centralisant des informations commerciales précieuses.
                <br />En tant que conseillers, nous continuons à accompagner Fashop dans l'évolution de leur solution qui permet
                aux fabricants et distributeurs européens de développer une distribution stratégique optimisée.<br />
                Notre expertise a contribué à créer un groupe leader
                dans la stratégie de distribution des marques de mode.
                </ProjectDescription>
                <ProjectMeta>E-commerce • B2B</ProjectMeta>
                <ProjectLink href="https://fashop.fr" target="_blank" rel="noopener noreferrer">Voir le projet</ProjectLink>
              </ProjectContent>
            </ProjectCard>

          </ProjectsGrid>
          
        </ProjectsSection>
        
      </MainSection>
      
      <Footer 
        pageContext="Page Projets"
        heading={
          <>
            Vous avez un projet ? 
            <span role="img" aria-label="waving hand">👋</span>
          </>
        }
        subheading="Discutons de votre projet."
        buttonText="Envoyez un message"
        buttonLink="/contact"
      />
    </PageContainer>
  );
};

export default Projets;